import { useEffect, type RefObject } from 'react';

import { CLICK_OUTSIDE_EVENT } from '../constants/event.constants';

/**
 * useClickOutside — fires a callback when a click lands outside the referenced element.
 *
 * Uses `mousedown` (not `click`) so the handler runs before focus changes,
 * matching the UX pattern used by dropdown menus and modals.
 *
 * The listener is only attached when `enabled` is true, avoiding
 * unnecessary document-level event subscriptions.
 *
 * @param ref      - React ref of the element to treat as "inside".
 * @param handler  - Callback invoked on outside click.
 * @param enabled  - Whether the listener is active (default: true).
 */
const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  handler: () => void,
  enabled = true,
): void => {
  useEffect(() => {
    if (!enabled) return;

    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    };

    document.addEventListener(CLICK_OUTSIDE_EVENT, onMouseDown);
    return () => document.removeEventListener(CLICK_OUTSIDE_EVENT, onMouseDown);
  }, [ref, handler, enabled]);
};

export default useClickOutside;
